/**
 * Void a subscription entered by mistake.
 *
 * Deliberately NOT a CancelDialog with a different label. A cancellation is a
 * real event — the member joined and stopped — and its refund is a negative
 * payment so the money movement stays on the record. A void says the sale
 * never happened at all, so it writes no payment of any kind; see the doc
 * comment on `voidSubscription` for why a matched pair of rows describing
 * money that never moved is exactly what breaks a till reconciliation.
 *
 * Gated on the manager PIN here, at the call site — `voidSubscription` itself
 * does no authorisation, it is just the write.
 */
import { useState } from 'react';
import { voidSubscription } from '../db/operations';
import { attemptPin } from '../auth/pin';
import { usePinLock } from '../auth/usePinLock';
import { classLabel } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import type { SubscriptionRow } from '../db/queries';

const PIN_LENGTH = 4;

export default function VoidDialog({
  row,
  onClose,
}: {
  row: SubscriptionRow;
  onClose: () => void;
}) {
  const sub = row.subscription;
  const [pin, setPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { locked, lockText, note } = usePinLock('manager');

  const canSave = pin.length === PIN_LENGTH && !saving && !locked;

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const attempt = await attemptPin(pin, 'manager');
      if (!attempt.ok) {
        setError(note(attempt));
        setPin('');
        setSaving(false);
        return;
      }
      await voidSubscription(sub.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not void');
      setSaving(false);
    }
  }

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="card max-h-[90vh] w-full max-w-md overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-2xl text-brand">Void subscription</h2>
        <p className="mb-5 text-sm text-white/40" dir="auto">
          {row.member.full_name} · {classLabel(sub.class_key)}
          {sub.private && ` · Private${sub.private_coach ? ` · ${sub.private_coach}` : ''}`}
          {' · '}
          {sub.months} {sub.months === 1 ? 'month' : 'months'} ·{' '}
          {formatEgp(sub.total_due_piastres, { hideZeroFraction: true })}
        </p>

        <div className="mb-4 rounded-btn border border-edge bg-black/30 p-4 text-sm text-white/60">
          For a subscription entered by mistake — wrong class, a duplicate, or
          typed onto the wrong member. This is <strong>not</strong> a
          cancellation: no refund is paid and no money is recorded as moving.
          The subscription and every payment against it disappear from
          revenue and payout reports entirely.
        </div>

        {row.amountPaid !== 0 && (
          <div className="mb-4 rounded-btn border border-status-due/40 bg-status-dueBg p-3 text-sm text-status-due">
            <strong className="block">
              {formatEgp(row.amountPaid, { hideZeroFraction: true })} was recorded as
              collected on this subscription.
            </strong>
            That amount will vanish from today’s takings — check the till
            against it before you continue.
          </div>
        )}

        <label className="label" htmlFor="voidPin">
          Manager PIN
        </label>
        <input
          id="voidPin"
          type="password"
          inputMode="numeric"
          maxLength={PIN_LENGTH}
          value={pin}
          autoFocus
          disabled={saving || locked}
          onChange={(e) => {
            setError('');
            setPin(e.target.value.replace(/\D/g, ''));
          }}
          className="field tabular text-center text-3xl tracking-[0.5em]"
          placeholder="••••"
        />

        {(locked || error) && (
          <p className="mt-4 rounded-btn border border-status-expired/40 bg-status-expiredBg p-3 text-sm text-status-expired">
            {locked ? lockText : error}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="btn-ghost flex-1">
            Keep it
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            className="min-h-touch flex-1 rounded-btn bg-status-expired px-4 text-sm font-bold
                       uppercase tracking-button text-paper disabled:opacity-30"
          >
            {saving ? 'Voiding…' : 'Void subscription'}
          </button>
        </div>

        <p className="mt-3 text-center text-xs leading-relaxed text-white/25">
          This cannot be undone from here. The record is soft-deleted, not
          destroyed.
        </p>
      </div>
    </div>
  );
}
